import { useEffect } from 'react';

const DEFAULT_TITLE = 'Go Rides | Premium Car Rental in Rwanda';
const DEFAULT_DESCRIPTION = 'Browse premium rentals, flexible booking, trusted fleet owners, and seamless reservations with Go Rides.';
const DEFAULT_IMAGE = '/logo512.png';
const SITE_ORIGIN = process.env.REACT_APP_SITE_URL || 'https://goridesrw.com';

const ensureTag = (selector, createTag) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = createTag();
    document.head.appendChild(element);
  }
  return element;
};

const setMeta = (key, value, attr = 'name') => {
  if (!value) return;
  const selector = `meta[${attr}="${key}"]`;
  const element = ensureTag(selector, () => {
    const meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    return meta;
  });
  element.setAttribute('content', value);
};

const setLink = (rel, href) => {
  if (!href) return;
  const selector = `link[rel="${rel}"]`;
  const element = ensureTag(selector, () => {
    const link = document.createElement('link');
    link.setAttribute('rel', rel);
    return link;
  });
  element.setAttribute('href', href);
};

const setStructuredData = (data) => {
  const id = 'seo-structured-data';
  let script = document.getElementById(id);
  if (!data) {
    if (script) {
      script.remove();
    }
    return;
  }

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

const SeoHead = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  canonicalPath = '/',
  type = 'website',
  robots = 'index, follow',
  structuredData = null
}) => {
  useEffect(() => {
    const origin = window.location.origin && window.location.origin !== 'null'
      ? window.location.origin
      : SITE_ORIGIN;
    const canonicalUrl = canonicalPath.startsWith('http') ? canonicalPath : `${origin}${canonicalPath}`;
    const imageUrl = image.startsWith('http') ? image : `${origin}${image}`;

    document.title = title;
    setMeta('description', description);
    setMeta('robots', robots);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', type, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', imageUrl, 'property');
    setMeta('og:site_name', 'Go Rides', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', imageUrl);
    setLink('canonical', canonicalUrl);
    setStructuredData(structuredData);

    return () => {
      setStructuredData(null);
    };
  }, [title, description, image, canonicalPath, type, robots, structuredData]);

  return null;
};

export default SeoHead;
